import React, { useState, useEffect } from 'react';
import Button from '../Button/Button';
import './LancerMarcheModal.css';
import { useModalData } from '../../hooks/useModalData';
import { useModalActions } from '../../hooks/useModalActions';
import ErreurPopUp from '../ErreurPopUp/ErreurPopUp';

interface TitulaireRang1 {
  id: number | string;
  rang: number;
  numero?: string | null;
  entite: { id: number; nom: string; logo_url?: string | null; siret?: string | null };
  lot_technique: { id: number; nom: string; code: string; image_url?: string | null };
  prestataire?: { nom: string; prenom: string; email: string; telephone: string; fonction: string } | null;
}

interface LancerMarcheModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  showNomOperation?: boolean;
  showLotTechnique?: boolean;
  showLotGeographique?: boolean;
  showDepartement?: boolean;
  mode?: 'MS' | 'BC';
  expire_at?: string;
  initialValues?: {
    lotTechnique?: string;
    lotGeographique?: string;
  };
}

const LancerMarcheModal: React.FC<LancerMarcheModalProps> = ({
  isOpen,
  onClose,
  title = "Lancer un marché subséquent",
  showNomOperation = true,
  showLotTechnique = true,
  showLotGeographique = true,
  showDepartement = false,
  mode = 'MS',
  initialValues,
}) => {
  const [formData, setFormData] = useState({
    nomOperation: '',
    lotTechnique: initialValues?.lotTechnique || '',
    lotGeographique: initialValues?.lotGeographique || '',
    departement: '',
    expire_at: '',
  });

  // Bloquer le scroll de la page quand la modale est ouverte
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isOpen]);

  // Synchroniser les valeurs pré-remplies à chaque ouverture ou changement de initialValues
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        nomOperation: '',
        lotTechnique: initialValues?.lotTechnique || '',
        lotGeographique: initialValues?.lotGeographique || '',
      }));
    }
  }, [isOpen, initialValues?.lotTechnique, initialValues?.lotGeographique]);

  const [errors, setErrors] = useState<{
    nomOperation?: string;
    lotTechnique?: string;
    lotGeographique?: string;
    departement?: string;
  }>({});

  // Titulaires (rang 1 pour BC, rangs 1/2/3 pour MS)
  const [titulaires, setTitulaires] = useState<TitulaireRang1[]>([]);
  const [titulaireLoading, setTitulaireLoading] = useState(false);

  useEffect(() => {
    const { lotTechnique, lotGeographique } = formData;
    if (!lotTechnique || !lotGeographique) { setTitulaires([]); return; }

    let cancelled = false;
    setTitulaireLoading(true);
    setTitulaires([]);

    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ departement: lotGeographique, lot: lotTechnique });
    fetch(`${process.env.REACT_APP_API_URL}/prestataire/annuaire?${params}`, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    })
      .then(r => r.json())
      .then(result => {
        if (cancelled) return;
        const contacts: TitulaireRang1[] = result?.data ?? [];
        if (mode === 'BC') {
          const rang1 = contacts.find((c: any) => c.rang === 1);
          setTitulaires(rang1 ? [rang1] : []);
        } else {
          const three = ([1, 2, 3].map(r => contacts.find((c: any) => c.rang === r) ?? null).filter(Boolean)) as TitulaireRang1[];
          setTitulaires(three);
        }
      })
      .catch(() => { if (!cancelled) setTitulaires([]); })
      .finally(() => { if (!cancelled) setTitulaireLoading(false); });

    return () => { cancelled = true; };
  }, [formData.lotTechnique, formData.lotGeographique, mode]);

  // États pour le modal d'erreur
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Utilisation des hooks personnalisés
  const {
    lotsTechniques,
    lotsGeographiques,
    departements,
    loading,
    error
  } = useModalData(isOpen, showLotTechnique, showLotGeographique, showDepartement);

  const { createOperation, isSubmitting } = useModalActions(onClose);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (showNomOperation && !formData.nomOperation.trim()) {
      newErrors.nomOperation = "Le nom de l'opération est requis";
    }

    if (showLotTechnique && !formData.lotTechnique) {
      newErrors.lotTechnique = "Le lot technique est requis";
    }

    if (showLotGeographique && !formData.lotGeographique) {
      newErrors.lotGeographique = "Le lot géographique est requis";
    }

    if (showDepartement && !formData.departement) {
      newErrors.departement = "Le département est requis";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        const expire_at = new Date(new Date().getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16);
        formData.expire_at = expire_at || '';
        await createOperation(formData, mode);
      } catch (error: any) {
        console.error('Erreur:', error);

        // Vérifier si c'est une erreur 400 avec un message
        if (error.status === 400 && error.message) {
          // Afficher le message dans le modal d'erreur
          setErrorMessage(error.message);
          setIsErrorModalOpen(true);
        } else {
          // Pour les autres erreurs, utiliser un message générique
          const errorMessage = error instanceof Error ? error.message :
            `Une erreur est survenue lors de la création du ${mode === 'BC' ? 'bon de commande' : 'marché subséquent'}.`;
          setErrorMessage(errorMessage);
          setIsErrorModalOpen(true);
        }
      }
    }
  };

  if (!isOpen) return null;

  const renderCard = (t: TitulaireRang1) => (
    <div key={String(t.id)} className="prestataire-card prestataire-card--readonly lmm-titulaire-card" style={{ width: '210px' }}>
      <div className="prestataire-card-inner">
        {/* Pastille "Titulaire rang X" masquee en marche subsequent (MS) ; conservee en BC. */}
        {mode !== 'MS' && <span className="prestataire-card-type">Titulaire rang {t.rang}</span>}
        <div className="prestataire-card-header">
          {t.lot_technique?.image_url ? (
            <img src={t.lot_technique.image_url} alt={t.lot_technique.nom} className="prestataire-card-banner-img" />
          ) : (
            <div className="prestataire-card-banner-placeholder" style={{ backgroundColor: '#f97316' }} />
          )}
        </div>
        <div className="prestataire-card-logo-wrapper">
          {t.entite?.logo_url ? (
            <img src={t.entite.logo_url} alt={t.entite.nom} className="prestataire-card-logo-img" />
          ) : (
            <span className="prestataire-card-logo-text">LOGO</span>
          )}
        </div>
        <div className="prestataire-card-body">
          <p className="prestataire-card-nom">{t.entite?.nom?.toUpperCase() || '-'}</p>
          {t.entite?.siret && (
            <p className="lmm-card-info"><span className="lmm-card-info-label">SIRET</span>{t.entite.siret}</p>
          )}
          {t.numero && (
            <p className="lmm-card-info"><span className="lmm-card-info-label">Contrat</span>n° {t.numero}</p>
          )}
          {t.prestataire && (t.prestataire.prenom || t.prestataire.nom) && (
            <p className="lmm-card-info lmm-card-info--contact">
              {t.prestataire.prenom} {t.prestataire.nom}
              {t.prestataire.fonction ? ` – ${t.prestataire.fonction}` : ''}
            </p>
          )}
          {t.prestataire?.email && (
            <p className="lmm-card-info"><a href={`mailto:${t.prestataire.email}`}>{t.prestataire.email}</a></p>
          )}
          {t.prestataire?.telephone && (
            <p className="lmm-card-info">{formatPhone(t.prestataire.telephone)}</p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="lancer-marche-modal" onClick={(e) => e.stopPropagation()}>
        <h2>{title}</h2>

        {/* {loading && <div className="loading-message">Chargement...</div>} */}
        {error && <div className="error-message">Erreur: {error}</div>}

        <form onSubmit={handleSubmit}>
          {showNomOperation && (
            <div className="form-group">
              <label>Nom de l'opération <span className="required">*</span></label>
              <input
                type="text"
                name="nomOperation"
                value={formData.nomOperation}
                onChange={handleChange}
                className={errors.nomOperation ? 'error' : ''}
                placeholder="Nom de l'opération"
              />
              {errors.nomOperation && <span className="error-message">{errors.nomOperation}</span>}
            </div>
          )}

          {showLotTechnique && (
            <div className="form-group">
              <label>Lot technique <span className="required">*</span></label>
              <select
                name="lotTechnique"
                value={formData.lotTechnique}
                onChange={handleChange}
                className={errors.lotTechnique ? 'error' : ''}
              >
                <option value="">Sélectionnez un lot technique</option>
                {lotsTechniques.map(lot => (
                  <option key={lot.id} value={lot.id}>
                    {lot.code ? `${lot.code} - ` : ''}{lot.nom || lot.nom}
                  </option>
                ))}
              </select>
              {errors.lotTechnique && <span className="error-message">{errors.lotTechnique}</span>}
            </div>
          )}

          {showLotGeographique && (
            <div className="form-group">
              <label>Lot géographique <span className="required">*</span></label>
              <select
                name="lotGeographique"
                value={formData.lotGeographique}
                onChange={handleChange}
                className={errors.lotGeographique ? 'error' : ''}
              >
                <option value="">Sélectionnez un département</option>
                {lotsGeographiques.length > 0 ? (
                  lotsGeographiques.map(dep => (
                    <option key={dep.id} value={dep.id_departement.toString()}>
                      {dep.id_departement} - {dep.departement?.nom || 'Département'}
                    </option>
                  ))
                ) : (
                  <option value="">Aucun département disponible</option>
                )}
              </select>
              {errors.lotGeographique && <span className="error-message">{errors.lotGeographique}</span>}
            </div>
          )}

          {showDepartement && (
            <div className="form-group">
              <label>Département <span className="required">*</span></label>
              <select
                name="departement"
                value={formData.departement}
                onChange={handleChange}
                className={errors.departement ? 'error' : ''}
              >
                <option value="">Sélectionnez un département</option>
                {departements.length > 0 ? (
                  departements.map(dep => (
                    <option key={dep.id} value={dep.id}>
                      {dep.nom}
                    </option>
                  ))
                ) : (
                  <option value="">Aucun département disponible</option>
                )}
              </select>
              {errors.departement && <span className="error-message">{errors.departement}</span>}
            </div>
          )}

          {/* Titulaires */}
          {(mode === 'BC' || mode === 'MS') && (formData.lotTechnique && formData.lotGeographique) && (
            <div className="lmm-titulaire-section">
              {titulaireLoading && (
                <div className="lmm-titulaire-loading">Recherche en cours…</div>
              )}
              {!titulaireLoading && titulaires.length === 0 && (
                <div className="lmm-titulaire-infructueux">
                  Désolé, ce lot a été infructueux. Nous n'avons pas de titulaire à proposer pour l'instant.
                </div>
              )}
              {!titulaireLoading && titulaires.length > 0 && (
                <div className="lmm-titulaire-row">
                  {mode === 'BC' ? (
                    <>
                      <div />
                      {renderCard(titulaires[0])}
                      <div />
                    </>
                  ) : (
                    titulaires.map(t => renderCard(t))
                  )}
                </div>
              )}
            </div>
          )}

          <div className="modal-actions">
            <Button
              value="Annuler"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            />
            <Button
              value={isSubmitting ? "Création en cours..." : "Continuer"}
              variant="primary"
              type="submit"
              disabled={isSubmitting || (
                (showNomOperation && !formData.nomOperation.trim()) ||
                (formData.lotTechnique !== '' && formData.lotGeographique !== '') &&
                !titulaireLoading &&
                titulaires.length === 0
              )}
            />
          </div>
        </form>
      </div>

      {/* Modal d'erreur */}
      <ErreurPopUp
        isOpen={isErrorModalOpen}
        message={errorMessage}
        onClose={() => setIsErrorModalOpen(false)}
      />
    </div>
  );
};

const formatPhone = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  let national = digits;
  if (digits.startsWith('33') && digits.length === 11) national = digits.slice(2);
  else if (digits.startsWith('0') && digits.length === 10) national = digits.slice(1);
  else return phone;
  return `+33 ${national[0]} ${national.slice(1, 3)} ${national.slice(3, 5)} ${national.slice(5, 7)} ${national.slice(7, 9)}`;
};

export default LancerMarcheModal;